import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Input,
  Button,
  Heading,
  CardBody,
  FormLabel,
  IconButton,
  FormControl,
  CurrencyInput,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency } from '../../../../../../helpers/formatters'
import { NATIONAL_AVG_MONTHLY_HOME_INSURANCE } from '../../Utilities/EstimatedMonthlyPaymentConstants'

export const MSG = defineMessages({
  homeInsurance: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsurance',
    defaultMessage: 'Home insurance'
  },
  homeInsuranceDescription: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceZipCodeDescription',
    defaultMessage: 'Based on ZIP code averages'
  },
  homeInsuranceNatlAvgDesc: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceNatlAvgDesc',
    defaultMessage: 'Based on national averages'
  },
  monthlyAmount: {
    id: 'EstimatedMonthlyPaymentBreakdown.monthlyAmount',
    defaultMessage: 'Monthly amount'
  },
  monthlyAmountDesc: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceMonthlyAmountDesc',
    defaultMessage: 'Most lenders require home insurance'
  },
  hide: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceHide',
    defaultMessage: 'Hide editable fields'
  },
  show: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceShow',
    defaultMessage: 'Show editable fields'
  },
  infoCardHeader: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceInfoCardHeader',
    defaultMessage: 'Get ahead of the game'
  },
  infoCardBody: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceInfoCardBody',
    defaultMessage: 'These days, insurance costs can be unpredictable. Get a quote early to avoid surprises and delays.'
  },
  zipCode: {
    id: 'EstimatedMonthlyPaymentBreakdown.zipCode',
    defaultMessage: 'ZIP code'
  },
  zipCodeDesc: {
    id: 'EstimatedMonthlyPaymentBreakdown.zipCodeDesc',
    defaultMessage: 'Get a better estimate for your area'
  },
  natlAvgCardHeader: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceNatlAvgCardHeader',
    defaultMessage: 'Using national average'
  },
  natlAvgCardBody: {
    id: 'EstimatedMonthlyPaymentBreakdown.homeInsuranceNatlAvgCardBody',
    defaultMessage:
      'This is the national average for home insurance. Enter your desired ZIP code to get a more accurate estimate for your area.'
  }
})
export interface EstimatedMonthlyPaymentBreakdownHomeInsuranceProps {
  zipCode: string
  homeInsurance: string
  isCalculatorsPage?: boolean
  setZipCode: (value: string) => void
  setHomeInsurance: (value: string) => void
  setHasUsedMortgageCalculator: (value: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownHomeInsurance = ({
  zipCode,
  setZipCode,
  homeInsurance,
  setHomeInsurance,
  isCalculatorsPage,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownHomeInsuranceProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(true)
  const isUsingNatlAvg = Math.trunc(Number(homeInsurance)) === NATIONAL_AVG_MONTHLY_HOME_INSURANCE

  return (
    <Card p={3}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
      >
        <Flex align='center' justify='space-between'>
          <Flex gap={2} align='center'>
            <Box bg='warning.700' width={3} height={3} borderRadius='50%' />
            <Heading as='h4' size={['2xs', 'xs', 'sm']}>
              {intl.formatMessage(MSG.homeInsurance)}
            </Heading>
          </Flex>
          <Flex>
            <Text
              mb={0}
              color='neutral.900'
              fontWeight='semibold'
              size={['xs', 'sm', 'md']}
              style={{ fontVariant: 'tabular-nums' }}
            >
              {formatCurrency(homeInsurance)}
            </Text>
            <IconButton
              size='xs'
              variant='link'
              onClick={() => setShowEditableFields(!showEditableFields)}
              aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
              icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
              tracking={{
                guid: 'wiAq24WVrd1GZvqWo2CRor',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator',
                descriptor: `Home Insurance ${showEditableFields ? 'Hide' : 'Show'}`
              }}
            />
          </Flex>
        </Flex>
        <Text
          ml={5}
          as='span'
          display='block'
          cursor='pointer'
          size={['2xs', 'xs', 'sm']}
          onClick={() => setShowEditableFields(!showEditableFields)}
        >
          {intl.formatMessage(isUsingNatlAvg ? MSG.homeInsuranceNatlAvgDesc : MSG.homeInsuranceDescription)}
        </Text>
      </Button>
      {showEditableFields && (
        <Box>
          {isUsingNatlAvg && (
            <>
              {/* @ts-ignore */}
              <Card mt={4} backgroundColor='var(--hb-colors-warning-50) !important'>
                <CardBody p={3} display='flex' gap={3}>
                  <Icon mt={1} color='warning.700' name='info-circle-fa' />
                  <Box>
                    <Heading mb={0} as='h5' size='2xs' textAlign='left'>
                      {intl.formatMessage(MSG.natlAvgCardHeader)}
                    </Heading>
                    <Text size='xs'>{intl.formatMessage(MSG.natlAvgCardBody)}</Text>
                  </Box>
                </CardBody>
              </Card>
            </>
          )}
          <FormControl mt={4}>
            <FormLabel size='xs'>{intl.formatMessage(MSG.zipCode)}</FormLabel>
            <Input
              size='sm'
              type='number'
              name='zipCode'
              value={zipCode}
              maxWidth='250px'
              onChange={e => setZipCode(e.target.value)}
              tracking={{
                descriptor: 'Zip Code',
                guid: '6XHfcAh9vZ8xQj8Nc2R3ky',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
              }}
            />
            <FormHelperText>
              <Text mb={0} size='3xs'>
                {intl.formatMessage(MSG.zipCodeDesc)}
              </Text>
            </FormHelperText>
          </FormControl>
          <FormControl mt={4}>
            <FormLabel size='xs'>{intl.formatMessage(MSG.monthlyAmount)}</FormLabel>
            <CurrencyInput
              size='sm'
              maxWidth='250px'
              decimalScale={2}
              name='homeInsurance'
              value={homeInsurance}
              // @ts-ignore
              onChange={val => setHomeInsurance(val)}
              onFocus={() => setHasUsedMortgageCalculator(true)}
              tracking={{
                descriptor: 'Home Insurance',
                guid: 'kFDfz6dPF23RjYF9PMAcqb',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
              }}
            />
            <FormHelperText>
              <Text mb={0} size='3xs'>
                {intl.formatMessage(MSG.monthlyAmountDesc)}
              </Text>
            </FormHelperText>
          </FormControl>
          {/* @ts-ignore */}
          <Card mt={4} backgroundColor='var(--hb-colors-primary-50) !important'>
            <CardBody p={3} display='flex' gap={3}>
              <Icon mt={1} color='primary.700' name='info-circle-fa' />
              <Box>
                <Heading mb={0} as='h5' size='2xs' textAlign='left'>
                  {intl.formatMessage(MSG.infoCardHeader)}
                </Heading>
                <Text size='xs'>{intl.formatMessage(MSG.infoCardBody)}</Text>
              </Box>
            </CardBody>
          </Card>
        </Box>
      )}
    </Card>
  )
}
